import {
	SYNTH_DEFAULT_ATTACK,
	SYNTH_GLIDE_IN,
	SYNTH_LOWPASS_HZ,
	SYNTH_MASTER_GAIN,
	SYNTH_SILENCE,
} from "@client/utils/synth/constants";
import type {
	AudioSessionNavigator,
	SynthNote,
	SynthSound,
	SynthStyle,
} from "@client/utils/synth/types";

let context: AudioContext | null = null;

function getContext(): AudioContext | null {
	if (typeof window === "undefined" || !("AudioContext" in window)) {
		return null;
	}
	if (!context) {
		const { audioSession } = navigator as AudioSessionNavigator;
		if (audioSession) {
			audioSession.type = "ambient";
		}
		context = new AudioContext();
	}
	if (context.state === "suspended") {
		void context.resume();
	}
	return context;
}

function scheduleVoice(
	audio: AudioContext,
	output: AudioNode,
	note: SynthNote,
	ratio: number,
	peak: number,
	duration: number,
	at: number,
) {
	const start = at + note.start;
	const attack = Math.min(note.attack ?? SYNTH_DEFAULT_ATTACK, duration / 2);
	const end = start + duration;
	const frequency = note.frequency * ratio;

	const oscillator = audio.createOscillator();
	oscillator.type = note.wave ?? "sine";
	if (note.glideFrom) {
		oscillator.frequency.setValueAtTime(note.glideFrom * ratio, start);
		oscillator.frequency.exponentialRampToValueAtTime(
			frequency,
			start + Math.min(SYNTH_GLIDE_IN, duration),
		);
	} else {
		oscillator.frequency.setValueAtTime(frequency, start);
	}
	if (note.glideTo) {
		oscillator.frequency.exponentialRampToValueAtTime(
			note.glideTo * ratio,
			end,
		);
	}

	const envelope = audio.createGain();
	envelope.gain.setValueAtTime(SYNTH_SILENCE, start);
	envelope.gain.linearRampToValueAtTime(peak, start + attack);
	envelope.gain.exponentialRampToValueAtTime(SYNTH_SILENCE, end);

	oscillator.connect(envelope).connect(output);
	oscillator.start(start);
	oscillator.stop(end + 0.02);
}

export function tone(
	frequency: number,
	start: number,
	duration: number,
	options: Partial<SynthNote> = {},
): SynthNote {
	return { frequency, start, duration, ...options };
}

export function noteHz(midi: number): number {
	return 440 * 2 ** ((midi - 69) / 12);
}

export function chord(
	midis: number[],
	start: number,
	duration: number,
	options: Partial<SynthNote> = {},
): SynthNote[] {
	return midis.map((midi) => tone(noteHz(midi), start, duration, options));
}

export function renderInStyle(
	motif: SynthNote[],
	style: SynthStyle,
): SynthSound {
	return {
		gain: style.gain,
		lowpass: style.lowpass,
		notes: motif.map((note) => {
			const duration = Math.min(
				Math.max(
					note.duration * style.durationScale,
					style.minDuration,
				),
				style.maxDuration ?? Infinity,
			);
			const frequency = note.frequency * style.octave;
			return {
				...note,
				frequency,
				duration,
				wave: style.wave,
				attack: style.attack,
				glideFrom: style.glideIn
					? frequency * style.glideIn
					: undefined,
				partials: style.partials?.map((partial) => ({
					ratio: partial.ratio,
					gain: partial.gain,
					duration: duration * partial.length,
				})),
			};
		}),
	};
}

export function getSynthLength(sound: SynthSound): number {
	return Math.max(
		0,
		...sound.notes.map((note) => note.start + note.duration),
	);
}

export function playSynth(sound: SynthSound, delay = 0) {
	const audio = getContext();
	if (!audio) {
		return;
	}
	const master = audio.createGain();
	master.gain.value = SYNTH_MASTER_GAIN * (sound.gain ?? 1);
	const filter = audio.createBiquadFilter();
	filter.type = "lowpass";
	filter.frequency.value = sound.lowpass ?? SYNTH_LOWPASS_HZ;
	master.connect(filter).connect(audio.destination);

	const at = audio.currentTime + 0.01 + delay;
	for (const note of sound.notes) {
		const peak = note.gain ?? 1;
		scheduleVoice(audio, master, note, 1, peak, note.duration, at);
		for (const partial of note.partials ?? []) {
			scheduleVoice(
				audio,
				master,
				note,
				partial.ratio,
				peak * partial.gain,
				partial.duration ?? note.duration,
				at,
			);
		}
	}
}
